/* eslint-disable react/no-multi-comp */
import { Field, Form, Formik, type FormikHelpers } from "formik";

import { useFlags } from "launchdarkly-react-client-sdk";
import { equals, isEmpty, map, path, pathOr } from "ramda";
import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { TrackJS } from "trackjs";
import Loader from "components/loader";
import MuiTextfield from "components/mui_textfield";
import Overlay from "components/overlay";
import MobileCreditCardSubmissionFrame from "components/payment/mobile_credit_card_submission_frame";
import {
  initialCardInfo,
  onCardNumberChange,
  onExpirationUpdate,
  onExpiryBlur,
  validateAddress1,
  validateCity,
  validateCreditCard,
  validateCvv,
  validateEmail,
  validateExpirationInput,
  validateFirstName,
  validateLastName,
  validatePhone,
  validateState,
  validateZip,
} from "helpers/credit_card_form";
import {
  getRecaptchaToken,
  loadGoogleRecaptcha,
} from "helpers/google_recaptcha";

import type {
  ICreditCardPaymentInfo,
  ICreditCardPostVaultingProps,
  IMerchantInfo,
} from "types/credit_card";
import { type RemoteData, RequestStatus } from "types/requests";

export const StyledWrapper = styled.div<IOptionalFields>`
  font-size: 1rem; // 14px based on 14px root font-size; decrease to .875rem for 16px root
  line-height: 1.5rem; // ~22.5px based on 14px root font-size; decrease to 1.33rem for 16px root
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
  grid-gap: 16px;
  grid-template-areas:
    "header header"
    "firstName lastName"
    "cardNumber cardNumber"
    "expiration cvv"
    "address1 address1"
    "address2 address2"
    "city city"
    "state zip"
    ${props => props.includeEmailField && "email email"}
    ${props => props.includePhoneField && "phone phone"}
    "terms terms"
    "button button";
`;

const StyledFormControl = styled.div<{ gridArea: string }>`
  grid-area: ${props => props.gridArea};
`;

const StyledSubheader = styled.h2`
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 1px;
  text-transform: uppercase;
  grid-area: header;
`;

const StyledFormButton = styled.div`
  min-height: 4rem;
  grid-area: button;

  button {
    font-size: 14px;
    line-height: 22px;
    font-weight: 600;
    background: #3e5b83;
    border-radius: 4px;

    &:disabled {
      cursor: pointer;
      background-color: #b5b5b5;
    }
  }
`;

const StyledErrorMessage = styled.div`
  display: none;
`;

const StyledSubmissionErrorMessage = styled(StyledErrorMessage)`
  display: block;
  position: relative;
  bottom: 10px;

  font-size: 16px;
  color: #e02948;
`;

const StyledProcessingLoader = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  height: 60%;
  width: 60%;
  padding-top: 100px;

  font-size: 18px;

  background-color: #ffffff;
  border-radius: 5px;

  text-align: center;
`;

export type CreditCardFormValues = {
  firstName: string;
  lastName: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  expiration: string;
  cvv: string;
  email: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
};

interface IOptionalFields {
  // TODO: https://upserve.atlassian.net/browse/HQ-7934
  // includeGratuityField?: boolean;
  includeEmailField?: boolean;
  includePhoneField?: boolean;
  externalSubmitDisabled?: boolean;
}

export interface ICreditCardFormProps<AfterResult> extends IOptionalFields {
  merchantInfo: IMerchantInfo;
  paymentInfo: ICreditCardPaymentInfo;
  afterProcessedPaymentRequest: RemoteData<AfterResult>;
  submitCreditCardPayment: (props: ICreditCardPostVaultingProps) => void;
  creditCardRequestStatus: RequestStatus;
  setCreditCardRequestStatus: (props: RequestStatus) => void;
}

export function DigitalGiftCardCreditCardForm<AfterResult>({
  merchantInfo,
  paymentInfo,
  afterProcessedPaymentRequest,
  creditCardRequestStatus,
  submitCreditCardPayment,
  setCreditCardRequestStatus,
  // TODO: https://upserve.atlassian.net/browse/HQ-7934
  // includeGratuityField,
  includeEmailField,
  includePhoneField,
  externalSubmitDisabled,
}: ICreditCardFormProps<AfterResult>) {
  const timeoutRef: React.MutableRefObject<ReturnType<
    typeof setTimeout
  > | null> = useRef(null);

  // TODO: https://upserve.atlassian.net/browse/HQ-7934
  // Tip handling coming soon...
  // const [tip, setTip] = useState<Money>(Money(0));
  // const [customTipError, setCustomTipError] = useState("");

  const [cardInfo, setCardInfo] = useState(initialCardInfo);

  let paymentError: ReactNode;
  if (equals(RequestStatus.FAILURE, creditCardRequestStatus)) {
    paymentError = (
      <StyledSubmissionErrorMessage className="error payment-error">
        {pathOr(
          "There was an error processing your request. Please check the form and try again.",
          ["reason"],
          afterProcessedPaymentRequest
        )}
      </StyledSubmissionErrorMessage>
    );
  }

  // form submission
  const ccSubmissionRef = useRef();

  const [ccFrameActive, setCcFrameActive] = useState(false);

  const handlePaymentMethodToken = useCallback(
    event => {
      if (!equals("upserve-payments", event.data.source)) {
        return;
      }
      if (timeoutRef.current) {
        // clear timeout after card vaulting
        clearTimeout(timeoutRef.current);
      }
      if (event.data.paymentMethodToken) {
        const { paymentMethodToken } = event.data;
        submitCreditCardPayment({
          paymentInfo,
          total: paymentInfo.balanceDue,
          paymentMethodToken,
          cardInfo,
          // tip,
        });
      } else {
        TrackJS.track("error vaulting credit card");
        setCreditCardRequestStatus(RequestStatus.FAILURE);
        setCcFrameActive(false);
      }
    },
    // added since a new eslint rule,
    // feel free to fix this eslint error if you can test it properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardInfo]
  );

  useEffect(() => {
    const paymentHandler = event => {
      handlePaymentMethodToken(event);
    };
    window.addEventListener("message", paymentHandler);
    loadGoogleRecaptcha();
    return () => {
      window.removeEventListener("message", paymentHandler);
    };
  }, [handlePaymentMethodToken]);

  const flags = useFlags();

  const formSubmit = useCallback(
    (
      values: CreditCardFormValues,
      { setSubmitting }: FormikHelpers<CreditCardFormValues>
    ): void => {
      setCreditCardRequestStatus(RequestStatus.REQUESTING);
      setCardInfo({ ...values });
      setCcFrameActive(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const doSubmit = path(["current", "doSubmit"], ccSubmissionRef);
      if (typeof doSubmit === "function") {
        if (flags.oloCreditCardRecaptcha) {
          const onError = () =>
            setCreditCardRequestStatus(RequestStatus.FAILURE);
          const onSuccess = doSubmit;

          getRecaptchaToken({ onError, onSuccess });
        } else {
          doSubmit();
        }

        timeoutRef.current = setTimeout(() => {
          TrackJS.track("credit card vaulting timed out after 30s");
          setCreditCardRequestStatus(RequestStatus.FAILURE);
          setCcFrameActive(false);
        }, 30000);
      } else {
        TrackJS.track(
          "credit card submission frame unavailable for submission"
        );
      }
      setSubmitting(false);
    },
    // added since a new eslint rule,
    // feel free to fix this eslint error if you can test it properly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ccSubmissionRef, creditCardRequestStatus, flags]
  );

  const ccFieldRef = useRef<HTMLInputElement>();

  // form inputs
  const inputs = [
    {
      name: "firstName",
      label: "First Name",
      autocomplete: "given-name",
      validate: validateFirstName,
    },
    {
      name: "lastName",
      label: "Last Name",
      autocomplete: "family-name",
      validate: validateLastName,
    },
    {
      name: "cardNumber",
      label: "Card Number",
      autocomplete: "cc-number",
      type: "number",
      validate: validateCreditCard,
      ref: ccFieldRef,
      onChange: onCardNumberChange,
    },
    {
      name: "expiration",
      label: "Expiration",
      autocomplete: "cc-exp",
      pattern: "[0-9]{2}/[0-9]{2}",
      type: "text",
      validate: validateExpirationInput,
      onChange: onExpirationUpdate,
      placeholder: "MM/YY",
      maxLength: 5, // this includes the "/"
      onBlur: onExpiryBlur,
    },
    {
      name: "cvv",
      label: "CVV",
      autocomplete: "cc-csc",
      validate: value =>
        validateCvv(
          value,
          ccFieldRef.current ? ccFieldRef.current.value : null
        ),
    },
    {
      name: "address1",
      label: "Address 1",
      autocomplete: "address-line1",
      validate: validateAddress1,
    },
    {
      name: "address2",
      label: "Address 2",
      placeholder: "Apartment, suite, unit, etc",
      autocomplete: "address-line2",
    },
    {
      name: "city",
      label: "City",
      autocomplete: "address-level2",
      validate: validateCity,
    },
    {
      name: "state",
      label: "State / Province",
      autocomplete: "address-level1",
      maxLength: 2,
      pattern: "[A-Za-z]+",
      validate: validateState,
    },
    {
      name: "zip",
      label: "Zip / Postal Code",
      autocomplete: "postal-code",
      pattern: "[A-Za-z0-9\\s]+",
      maxLength: 10,
      validate: validateZip,
    },
    // TODO: https://upserve.atlassian.net/browse/HQ-7934
    // includeGratuityField
    //   ? {
    //       name: "gratuity-placeholder",
    //     }
    //   : {},
    includeEmailField
      ? {
          name: "email",
          label: "Email for Receipt",
          autocomplete: "email",
          type: "email",
          validate: validateEmail,
        }
      : {},
    includePhoneField
      ? {
          name: "phone",
          label: "Phone Number (Optional)",
          autocomplete: "tel",
          type: "tel",
          validate: validatePhone,
        }
      : {},
  ].filter(x => !isEmpty(x));

  const submitDisabled = (isValid, dirty) => {
    const isFormInvalid =
      !isValid ||
      (equals(RequestStatus.NOT_STARTED, creditCardRequestStatus) && !dirty);
    // TODO: https://upserve.atlassian.net/browse/HQ-7934
    // const isTipInvalid = !equals(customTipError, "");
    return isFormInvalid; // || isTipInvalid;
  };

  return (
    <>
      {equals(RequestStatus.REQUESTING, creditCardRequestStatus) && (
        <Overlay>
          <StyledProcessingLoader>
            <Loader>We're processing your card...</Loader>
          </StyledProcessingLoader>
        </Overlay>
      )}
      {ccFrameActive && (
        <MobileCreditCardSubmissionFrame
          key="ccSubmission"
          ref={ccSubmissionRef}
          card={cardInfo}
          merchantKey={merchantInfo.ugMerchantKey}
          hasRecaptchaInput={flags.oloCreditCardRecaptcha}
        />
      )}
      <Formik
        initialValues={{
          firstName: cardInfo.firstName,
          lastName: cardInfo.lastName,
          cardNumber: cardInfo.cardNumber,
          expiryMonth: cardInfo.expiryMonth,
          expiryYear: cardInfo.expiryYear,
          expiration:
            cardInfo.expiryYear &&
            cardInfo.expiryMonth &&
            `${cardInfo.expiryMonth}/${cardInfo.expiryYear}`,
          cvv: cardInfo.cvv,
          email: cardInfo.email,
          phone: cardInfo.phone,
          address1: cardInfo.address1,
          address2: cardInfo.address2,
          city: cardInfo.city,
          state: cardInfo.state,
          zip: cardInfo.zip,
        }}
        onSubmit={formSubmit}
        validateOnBlur>
        {({
          isValid,
          dirty,
          setFieldValue,
          handleChange,
          handleBlur,
          setFieldTouched,
        }) => (
          <Form translate="yes">
            <StyledWrapper
              {...{
                includeEmailField,
                includePhoneField,
                // TODO: https://upserve.atlassian.net/browse/HQ-7934
                // includeGratuityField,
              }}>
              <StyledSubheader>Credit Card</StyledSubheader>
              {map(inputDefinition => {
                // TODO: https://upserve.atlassian.net/browse/HQ-7934
                // if (inputDefinition.name === "gratuity-placeholder") {
                //   return (
                //     <GratuityComponent
                //       key="gratuity component"
                //       merchantInfo={merchantInfo}
                //       paymentInfo={paymentInfo}
                //       setTip={setTip}
                //       setCustomTipError={setCustomTipError}
                //       customTipError={customTipError}
                //     />
                //   );
                // }

                function makeControl(input) {
                  return (
                    <StyledFormControl
                      className={input.name}
                      key={input.name}
                      gridArea={input.name}>
                      <Field name={input.name} validate={input.validate}>
                        {({ meta }) => (
                          <MuiTextfield
                            error={meta.error && meta.touched}
                            errorMsg={
                              meta.error && meta.touched ? meta.error : ""
                            }
                            required={
                              !equals(input.name, "phone") &&
                              !equals(input.name, "email") &&
                              !equals(input.name, "address2")
                            }
                            name={input.name}
                            label={input.label}
                            value={meta.value}
                            InputProps={{
                              autoComplete: input.autocomplete,
                              pattern: input.pattern,
                              min: input.min,
                              max: input.max,
                            }}
                            inputRef={input.ref}
                            placeholder={input.placeholder}
                            maxLength={input.maxLength}
                            onChange={event => {
                              if (input.onChange) {
                                input.onChange(event, setFieldValue);
                              } else handleChange(event);
                            }}
                            onBlur={event => {
                              if (input.onBlur) {
                                input.onBlur(
                                  event,
                                  setFieldValue,
                                  setFieldTouched
                                );
                              } else handleBlur(event);
                            }}
                            type={input.type}
                          />
                        )}
                      </Field>
                    </StyledFormControl>
                  );
                }

                return makeControl(inputDefinition);
              }, inputs)}

              <div className="upserve-dgc-terms" style={{ gridArea: "terms" }}>
                <p>
                  By clicking on “Submit Order” you’ll agree to the
                  <br />
                  <a
                    href="https://assets-upserve.s3.amazonaws.com/terms-and-conditions/digital-gift-cards-terms-and-conditions.html"
                    rel="noopener noreferrer"
                    target="_blank">
                    Terms and Conditions
                  </a>
                  , and the credit card above will be charged.
                </p>
              </div>

              <StyledFormButton>
                {paymentError}
                <button
                  type="submit"
                  disabled={
                    submitDisabled(isValid, dirty) ||
                    externalSubmitDisabled === true
                  }>
                  Submit Order ({`${paymentInfo.balanceDue.format()}`})
                </button>
              </StyledFormButton>
            </StyledWrapper>
          </Form>
        )}
      </Formik>
    </>
  );
}
